import type { ActionCommand, DraftCommand } from '../commands.ts';
import { diag } from '../content/compendium.ts';
import { checkSeatCommand, HANDLERS } from '../reduce.ts';
import type { ActionOutcome, DraftAction, PublicState, Role, TableState, TurnReceipt } from '../types.ts';
import { ensure, eventActive, hasAccess, nextStep, seatedRoles, table, type Ctx } from './common.ts';
import { resolveStage } from './resolve-drafts.ts';
import { leaveArrivals, leaveHuddle } from './events.ts';
import { transferTarget } from './transfers.ts';
import { resolveDepartures } from './overflow.ts';
import { normaliseRoute } from './outcomes.ts';
import { locumPresent, scannerBox } from './act.ts';

/** Group intentions by their fixed resolution stage. */
export function actionStage(cmd: ActionCommand): 'LOGISTICS' | 'CLINICAL' | 'DEPARTURE' | 'TRANSFER' {
  if (cmd.type === 'REFER' || cmd.type === 'STEP_DOWN') return 'TRANSFER';
  if (cmd.type === 'DISCHARGE' || cmd.type === 'EARLY_DISCHARGE' || (cmd.type === 'USE_LOCUM' && cmd.action === 'DISCHARGE')) return 'DEPARTURE';
  if (['BOOK', 'CALL_FOR_HELP', 'TIME_OUT', 'ALLOCATE', 'SET_DIVERT', 'EVENT_CHOICE', 'PASS_LEAD', 'ASSIGN_COVER', 'MARK_DISPUTED', 'LEND_STAFF', 'PIN_STAFF', 'ORDER_BLOOD'].includes(cmd.type)) return 'LOGISTICS';
  return 'CLINICAL';
}

/** Count scanner steps from frozen known intent without consulting results. */
export function usesScanner(action: DraftAction): boolean {
  if (action.cmd.type === 'SCAN') return true;
  const intent = action.intent;
  if (action.cmd.type !== 'TREAT' || !intent?.route || intent.step === null) return false;
  const route = diag(intent.diagnosis).routes.find(r => r.code === intent.route);
  return route?.steps[intent.step]?.kind === 'SCAN';
}

/** Isolate mutable public planning state without copying hidden backs or other tables. */
export function createPlanPreview(pub: PublicState, tableNo: number): PublicState {
  const source = table(pub, tableNo);
  const current: TableState = { ...source,
    seats: Object.fromEntries(Object.entries(source.seats).map(([role, st]) => [role, { ...st, actions: { ...st.actions } }])),
    board: structuredClone(source.board), effects: structuredClone(source.effects), timeOut: { ...source.timeOut },
    locums: source.locums.map(l => ({ ...l })), bookings: source.bookings.map(b => ({ ...b })),
    prompts: source.prompts.slice(), interrupts: source.interrupts.map(i => ({ ...i })),
  };
  const cover = source.drafts.PHYS?.actions.find(a => a.cmd.type === 'ASSIGN_COVER' && !checkSeatCommand(pub, tableNo, 'PHYS', a.cmd))?.cmd;
  if (pub.clock.phase === 'ACT' && cover?.type === 'ASSIGN_COVER') current.effects.cover = cover.to;
  const patients = Object.fromEntries(Object.entries(pub.patients).map(([id, p]) => [id, p.table === tableNo && p.status === 'ACTIVE' ? structuredClone(p) : p]));
  const { v, config, seq, rng, counters, clock, script, room } = pub;
  return { v, config, seq, rng, counters, clock, script, room, patients, tables: pub.tables.map(t => t.no === tableNo ? current : t) };
}

/** Preserve seat order within the engine's fixed resolution stages. */
export function orderedPlan<T extends { cmd: ActionCommand }>(actions: readonly T[]): T[] {
  const order = { LOGISTICS: 0, CLINICAL: 1, DEPARTURE: 2, TRANSFER: 3 };
  return [...actions].sort((a, b) => order[actionStage(a.cmd)] - order[actionStage(b.cmd)]);
}

/** Reserve known route progress and resources without executing clinical outcomes. */
export function previewAction(preview: PublicState, tableNo: number, role: Role, cmd: ActionCommand): void {
  const t = table(preview, tableNo);
  const st = t.seats[role]!;
  const h = HANDLERS[cmd.type];
  if (t.timeOut.turn === preview.clock.turn && actionStage(cmd) !== 'LOGISTICS') return;
  if (checkSeatCommand(preview, tableNo, role, cmd, true)) return;
  const cost = h.cost(preview, t, role, cmd as never);
  st.actions.available -= cost;
  st.actions.spent += cost;
  if (cmd.type === 'CALL_FOR_HELP' || (cmd.type === 'BOOK' && cmd.kind === 'LOCUM')) t.callsForHelp++;
  if (cmd.type === 'BOOK' && cmd.kind !== 'LOCUM') {
    const turn = preview.clock.turn;
    t.bookings.push({ kind: cmd.kind, bookedTurn: turn, from: turn + 2, until: turn + 3, used: 0, request: 'PLANNED' });
  }
  if (cmd.type === 'TIME_OUT') t.timeOut = { used: true, turn: preview.clock.turn };
  if (cmd.type === 'ALLOCATE') {
    if (cmd.slot === 'theatre') t.board.allocations.theatre = cmd.to;
    else t.board.allocations.scanner[cmd.slot === 'scanner0' ? 0 : 1] = cmd.to;
  }
  if (cmd.type === 'ANAESTHETISE') t.board.theatre.anaesthetic = role;
  if (cmd.type === 'RED_BLANKET') t.redBlanketTurn = preview.clock.turn;
  if (cmd.type === 'FREE_ASSESS') st.freeAssessUsedTurn = preview.clock.turn;
  if (cmd.type === 'SECOND_OPINION') st.e22UsedTurn = preview.clock.turn;
  if (cmd.type === 'USE_LOCUM') t.locums[locumPresent(preview, t)!.idx]!.actionsLeft--;
  const p = 'p' in cmd && cmd.p ? preview.patients[cmd.p] : undefined;
  if (!p) return;
  if (cmd.type === 'START_ROUTE') p.route = { diag: p.believedKey, code: cmd.code, next: 0, startedTurn: preview.clock.turn };
  if (cmd.type === 'STABILISE' || (cmd.type === 'USE_LOCUM' && cmd.action === 'STABILISE')) p.needsStabilise = false;
  const step = nextStep(p);
  const treating = cmd.type === 'TREAT' || (cmd.type === 'USE_LOCUM' && cmd.action === 'TREAT');
  const box = cmd.type === 'SCAN' || (cmd.type === 'TREAT' && step?.kind === 'SCAN') ? scannerBox(preview, t, role) : -1;
  if (box >= 0) t.board.scannerUsedBy[box] = role;
  if (cmd.type === 'SUPPORT') p.turnScratch.supports = (p.turnScratch.supports ?? 0) + 1;
  if ((cmd.type === 'DISCHARGE' || cmd.type === 'EARLY_DISCHARGE') && cost === 0) t.effects.e7FirstDischargeUsed.push(eventActive(preview, 'E7') ? role : p.seat!);
  if (cmd.type === 'OPERATE') {
    if (cmd.theatre === 1 && t.board.secondTheatre) Object.assign(t.board.secondTheatre, { used: true, ventTaken: !!t.board.vents });
    else t.board.theatre.booking = { patient: p.id, surgeon: role };
  }
  if (p.route && ((treating && (step?.kind === 'TREAT' || step?.kind === 'REASSURE')) || ((cmd.type === 'OPERATE' || cmd.type === 'RED_BLANKET') && step?.kind === 'THEATRE'))) {
    p.route.next++;
    if (step?.warn) p.turnScratch.warnRoute = p.route.code;
    if (step?.blood && t.board.blood) t.board.blood.units -= step.blood;
  }
  if (p.route?.code !== 'X') normaliseRoute(p);
}

/** Preview the seat's known care sequence without clinical outcomes. */
export function plannedState(pub: PublicState, tableNo: number, role: Role, actions?: ActionCommand[]): PublicState {
  const preview = createPlanPreview(pub, tableNo);
  const commands = actions?.map(cmd => ({ cmd })) ?? table(pub, tableNo).drafts[role]?.actions ?? [];
  for (const { cmd } of orderedPlan(commands)) previewAction(preview, tableNo, role, cmd);
  return preview;
}

/** Save revision-checked planning metadata without executing care. */
export function runDraftCommand(ctx: Ctx, tableNo: number, role: Role, cmd: DraftCommand): void {
  ensure(ctx.s.clock.phase === 'ACT' && !ctx.s.clock.paused, 'You can change your plan only during the Action phase.');
  ensure(cmd.turn === ctx.s.clock.turn, 'That plan is from another turn.');
  const t = table(ctx.s, tableNo);
  const plan = t.drafts[role];
  ensure(t.seats[role] && plan, 'You are not seated.');
  ensure(plan.revision === cmd.expectedRevision, 'Your plan has changed since you last saw it. Check the latest saved plan.');
  if (cmd.type === 'SET_READY') {
    plan.ready = cmd.ready;
    plan.revision++;
    return;
  }
  if (cmd.type === 'RESPOND_REFERRAL') {
    const offer = t.drafts[cmd.sender]?.actions.find(a => a.id === cmd.actionId);
    ensure(offer && transferTarget(ctx.s, offer) === role && offer.revision === cmd.offerRevision, 'That referral has changed. Check it again.');
    t.consents = t.consents.filter(c => c.sender !== cmd.sender || c.actionId !== cmd.actionId);
    if (cmd.mode) t.consents.push({ sender: cmd.sender, receiver: role, actionId: cmd.actionId, offerRevision: cmd.offerRevision, mode: cmd.mode });
    plan.ready = false;
    plan.revision++;
    t.drafts[cmd.sender]!.ready = false;
    t.drafts[cmd.sender]!.revision++;
    return;
  }
  replaceDraft(ctx, t, role, cmd);
}

/** Invalidate readiness wherever changed intent alters shared commitments. */
function replaceDraft(ctx: Ctx, t: TableState, role: Role, cmd: Extract<DraftCommand, { type: 'SET_DRAFT' }>): void {
  const plan = t.drafts[role]!;
  const old = plan.actions;
  ensure(new Set(cmd.actions.map(a => a.id)).size === cmd.actions.length, 'Each draft action needs a unique ID.');
  const preview = createPlanPreview(ctx.s, t.no);
  const entries = new Map<string, typeof old[number]>();
  for (const a of orderedPlan(cmd.actions)) {
    ensure(a.cmd.type !== 'OPEN_CARD' && a.cmd.type !== 'CLOSE_CARD', 'Sharing a card happens straight away, not as part of your plan.');
    const p = 'p' in a.cmd && a.cmd.p ? ctx.s.patients[a.cmd.p] : undefined;
    if ('p' in a.cmd && a.cmd.p) ensure(p && hasAccess(ctx.s, t, role, p), 'You cannot see that patient’s card.');
    if (a.cmd.type === 'REFER' || a.cmd.type === 'STEP_DOWN') ensure(p?.seat === role, 'Only the team looking after this patient can refer them.');
    if (a.cmd.type === 'STEP_DOWN') ensure(role === 'ICU', 'Only ICU can offer a step-down.');
    const previous = old.find(entry => entry.id === a.id);
    const known = p ? preview.patients[p.id] : undefined;
    const intent = known ? { diagnosis: known.believedKey, route: known.route?.code ?? null, step: known.route?.next ?? null } : null;
    const stage = actionStage(a.cmd);
    const position = cmd.actions.filter(entry => actionStage(entry.cmd) === stage).findIndex(entry => entry.id === a.id);
    const oldPosition = old.filter(entry => actionStage(entry.cmd) === stage).findIndex(entry => entry.id === a.id);
    const unchanged = previous && position === oldPosition && JSON.stringify([previous.cmd, previous.intent, previous.priority]) === JSON.stringify([a.cmd, intent, a.priority]);
    entries.set(a.id, { ...a, revision: unchanged ? previous.revision : plan.revision + 1, intent });
    previewAction(preview, t.no, role, a.cmd);
  }
  plan.actions = cmd.actions.map(a => entries.get(a.id)!);
  const changed = [...old, ...plan.actions].filter(a => !(old.includes(a) ? plan.actions : old).some(b => b.id === a.id && b.revision === a.revision));
  const affected = new Set(changed.map(a => transferTarget(ctx.s, a)).filter((r): r is Role => !!r));
  for (const a of changed) if (a.cmd.type === 'ASSIGN_COVER') affected.add(a.cmd.to);
  if (changed.some(a => a.cmd.type === 'TIME_OUT' || a.cmd.type === 'ALLOCATE')) seatedRoles(t).forEach(r => affected.add(r));
  if (changed.some(a => a.cmd.type === 'OPERATE' && a.cmd.theatre !== 1)) affected.add('ICU');
  if (changed.some(a => a.cmd.type === 'ANAESTHETISE' && a.cmd.theatre !== 1)) affected.add('SURG');
  affected.delete(role);
  for (const r of affected) {
    const dependent = t.drafts[r];
    if (!dependent) continue;
    dependent.ready = false;
    dependent.revision++;
  }
  t.consents = t.consents.filter(c => c.sender !== role || plan.actions.some(a => a.id === c.actionId && a.revision === c.offerRevision));
  plan.ready = false;
  plan.revision++;
}

/** Open empty drafts for the released turn. */
export function startDraftTurn(ctx: Ctx): void {
  for (const t of ctx.s.tables) {
    t.consents = [];
    t.drafts = Object.fromEntries(seatedRoles(t).map(role => [role, { turn: ctx.s.clock.turn, revision: 0, ready: false, actions: [] }]));
  }
}

/** Execute the frozen plans once in deterministic seat order. */
export function commitDrafts(ctx: Ctx): ActionOutcome[][] {
  const outcomes: ActionOutcome[][] = ctx.s.tables.map(() => []);
  for (const stage of ['LOGISTICS', 'CLINICAL', 'DEPARTURE'] as const) {
    if (stage === 'CLINICAL') { leaveArrivals(ctx); leaveHuddle(ctx); }
    ctx.s.tables.forEach((t, index) => {
      const queues = Object.fromEntries(seatedRoles(t).map(role => [role, (t.drafts[role]?.actions ?? []).filter(a => actionStage(a.cmd) === stage)]));
      if (stage !== 'LOGISTICS' && t.timeOut.turn === ctx.s.clock.turn) {
        const cancelled = seatedRoles(t).flatMap(role => (queues[role] ?? []).map(a => ({ seat: role, actionId: a.id, status: 'CANCELLED' as const, reason: 'The Lead called a Time-Out.' })));
        outcomes[index]!.push(...cancelled);
      } else outcomes[index]!.push(...(stage === 'DEPARTURE' ? resolveDepartures(ctx, t, queues) : resolveStage(ctx, t, queues)));
    });
  }
  return outcomes;
}

/** Retain both boundaries of the current and previous turn. */
export function recordBoundary(ctx: Ctx, boundary: TurnReceipt['boundary'], start: number, outcomes: ActionOutcome[][]): void {
  ctx.s.tables.forEach((t, i) => {
    const turn = ctx.s.clock.turn;
    const events = ctx.events.slice(start).filter(e => e.table === null || e.table === t.no);
    t.receipts = [...t.receipts.filter(r => r.turn >= turn - 1), { id: `${turn}:${boundary}`, turn, boundary, events, outcomes: outcomes[i] ?? [] }];
  });
}
